import type { LicenseAdditionalFeatureName } from '../license/licenseSchema';
import type { ApiScopeRequirement } from '../rbac/ApiScopeRequirement';
import { makeTypeGuardByProperty } from '../types/index';

export const oasWebPageGroups = [
  'inventory',
  'technology',
  'management',
  'settings',
] as const;

export type OasWebPageGroup = (typeof oasWebPageGroups)[number];

export type OasWebCustomPageId = 'manualSiteSeparation';

export type OasWebPageTreeNodeBase = {
  title: string;
  'x-required-feature-flag'?: string;
  'x-require-license-feature'?: LicenseAdditionalFeatureName;
};

export type OasWebPageApiTable = OasWebPageTreeNodeBase & {
  apiTable: string;
};

export const isOasWebPageApiTable = makeTypeGuardByProperty<
  OasWebPageTreeNode,
  OasWebPageApiTable
>('apiTable');

export type OasWebPageCustom = OasWebPageTreeNodeBase & {
  apiScopeRequirement?: ApiScopeRequirement;
  customPage: OasWebCustomPageId;
  path: string;
};

export const isOasWebPageCustom = makeTypeGuardByProperty<
  OasWebPageTreeNode,
  OasWebPageCustom
>('customPage');

export type OasWebPage = OasWebPageApiTable | OasWebPageCustom;

export const isOasWebPage = (x: OasWebPageTreeNode): x is OasWebPage =>
  isOasWebPageCustom(x) || isOasWebPageApiTable(x);

export type OasWebTab = (OasWebPage | OasWebTabsPage) & {
  default?: boolean;
};

export type OasWebTabsPage = OasWebPageTreeNodeBase & {
  path: string;
  tabs: OasWebTab[];
};

export const isOasWebTabsPage = makeTypeGuardByProperty<
  OasWebPageTreeNode,
  OasWebTabsPage
>('tabs');

export type OasWebPageTreeNode = OasWebPage | OasWebTabsPage | OasWebMenuNode;

export const isOasWebMenuNode = makeTypeGuardByProperty<
  OasWebPageTreeNode,
  OasWebMenuNode
>('submenu');

export type OasWebMenuNode = OasWebPageTreeNodeBase & {
  submenu: OasWebPageTreeNode[];
};

/**
 * Define part of web dynamically generated from OAS
 *
 * At the time of writing, this is intended to be used for web sections
 * containing mainly canonical pages of API tables (Technology for sure, we will
 * see about Inventory, Management and Reports).
 *
 * We now intend to design it future-proof, to be extendable to any other kind
 * of web menu structure managed by OAS.
 *
 * It is represented in webPagesSchema.json in order to add intellisense to the
 * Yaml file. Any change to this file should be reflected in webPagesSchema.json.
 */
export type OasWebPages = { [key in OasWebPageGroup]: OasWebPageTreeNode[] };
